/**
 * GuestbookList — 방문자용 방명록 목록 (cursor pagination + 비밀번호 삭제)
 */

import { useState } from 'react'
import { useGuestbookPublic, useGuestbookDeleteByPassword } from '@/hooks/useGuestbook.js'
import type { GuestbookEntryPublic } from '@cugumi/shared/schemas'
import { Modal, Button } from '@/components/ui/index.js'
import { GuestbookEntryCard } from './GuestbookEntryCard.js'
import styles from '../GuestbookBlock.module.css'

interface GuestbookListProps {
  invitationId: string
  onMountAddOptimistic?: (fn: (entry: GuestbookEntryPublic) => void) => void
}

export function GuestbookList({ invitationId, onMountAddOptimistic }: GuestbookListProps) {
  const { entries, total, hasMore, isLoading, loadMore, addOptimistic } = useGuestbookPublic(invitationId)
  const { remove, isLoading: isDeleting, error: deleteError } = useGuestbookDeleteByPassword(invitationId)

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  // 부모에게 addOptimistic 함수 전달
  onMountAddOptimistic?.(addOptimistic)

  const handleDelete = async () => {
    if (!deleteTarget || !password.trim()) {
      setLocalError('비밀번호를 입력해주세요')
      return
    }
    setLocalError(null)
    const ok = await remove(deleteTarget, password)
    if (ok) {
      setDeleteTarget(null)
      setPassword('')
      // 페이지 새로고침으로 목록 갱신 (간단한 방법)
      window.location.reload()
    }
  }

  const handleCloseModal = () => {
    setDeleteTarget(null)
    setPassword('')
    setLocalError(null)
  }

  if (!isLoading && entries.length === 0) {
    return <p className={styles.emptyText}>아직 방명록이 없습니다</p>
  }

  return (
    <div className={styles.list}>
      {total > 0 && (
        <p className={styles.listHeader}>방명록 {total}개</p>
      )}
      {entries.map((entry) => (
        <GuestbookEntryCard
          key={entry.id}
          entry={entry}
          showDeleteBtn
          onDelete={setDeleteTarget}
        />
      ))}
      {hasMore && (
        <button
          type="button"
          className={styles.loadMoreBtn}
          onClick={loadMore}
          disabled={isLoading}
        >
          {isLoading ? '불러오는 중...' : '더보기'}
        </button>
      )}

      {/* 비밀번호 확인 모달 */}
      <Modal open={deleteTarget !== null} onClose={handleCloseModal} title="방명록 삭제" size="sm">
        <div className={styles.deleteModal}>
          <p className={styles.deleteModalText}>작성 시 입력한 비밀번호를 입력해주세요</p>
          <input
            className={styles.formInput}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="비밀번호"
            onKeyDown={(e) => e.key === 'Enter' && handleDelete()}
          />
          {(localError || deleteError) && (
            <p className={styles.formError}>{localError || deleteError}</p>
          )}
          <div className={styles.deleteModalActions}>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isDeleting}>
              취소
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
              삭제
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
